import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY } from '../config';

const DESIGNATIONS = [
  'Office Boy', 'Peon', 'Guard', 'Cleaner',
  'Driver', 'Cook', 'Helper', 'Other',
];

const DESIGNATION_COLORS = {
  'Peon':      'bg-blue-100 text-blue-800',
  'Guard':     'bg-red-100 text-red-800',
  'Cleaner':   'bg-teal-100 text-teal-800',
  'Driver':    'bg-indigo-100 text-indigo-800',
  'Cook':      'bg-orange-100 text-orange-800',
  'Office Boy':'bg-purple-100 text-purple-800',
  'Helper':    'bg-cyan-100 text-cyan-800',
  'Other':     'bg-gray-100 text-gray-700',
};


export default function StaffManagement() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = ['admin', 'superintendent', 'principal', 'accountant'].includes(role);

  const [staff, setStaff] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCampus, setFilterCampus] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', designation: 'Peon',
    phone: '', address: '', cnic: '', join_date: '',
    salary: 0, campus_id: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get('/staff/'),
        api.get('/campuses/').catch(() => ({ data: [] })),
      ]);
      setStaff(s.data || []);
      setCampuses(c.data || []);
    } catch (err) {
      console.error('Failed to load staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCampusName = (id) => campuses.find(x => x.id === id)?.name || '—';

  // ─── Submit ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name) {
      setError('First and last name are required.');
      return;
    }
    if (!form.designation) {
      setError('Designation is required.');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        designation: form.designation,
        phone: form.phone || '',
        address: form.address || '',
        cnic: form.cnic || null,
        join_date: form.join_date || null,
        salary: parseFloat(form.salary) || 0,
        campus_id: parseInt(form.campus_id) || (campuses[0]?.id || 1),
      };

      if (editing) {
        await api.put(`/staff/${editing.id}`, payload);
      } else {
        await api.post('/staff/', payload);
      }
      setModalOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({
      first_name: '', last_name: '', designation: 'Peon',
      phone: '', address: '', cnic: '', join_date: '',
      salary: 0, campus_id: '',
    });
    setEditing(null);
    setError('');
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      first_name: s.first_name,
      last_name: s.last_name,
      designation: s.designation || 'Peon',
      phone: s.phone || '',
      address: s.address || '',
      cnic: s.cnic || '',
      join_date: s.join_date || '',
      salary: s.salary || 0,
      campus_id: s.campus_id || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Deactivate ${s.first_name} ${s.last_name}?`)) return;
    try {
      await api.delete(`/staff/${s.id}`);
      fetchAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ─── Filtering ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    return staff.filter(s => {
      if (filterCampus !== 'all' && s.campus_id !== parseInt(filterCampus)) return false;
      if (filterDesignation !== 'all' && s.designation !== filterDesignation) return false;
      if (search) {
        const q = search.toLowerCase();
        const blob = `${s.first_name} ${s.last_name} ${s.phone || ''} ${s.cnic || ''} ${s.designation || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [staff, filterCampus, filterDesignation, search]);

  // ─── Campus counts ──────────────────────────────────────────
  const campusCounts = useMemo(() => {
    return campuses.map(c => ({
      ...c,
      count: staff.filter(s => s.campus_id === c.id).length,
    }));
  }, [campuses, staff]);

  // ─── Stats ──────────────────────────────────────────────────
  const totalMonthlySalary = filtered.reduce((sum, s) => sum + (s.salary || 0), 0);
  const uniqueDesignations = new Set(filtered.map(s => s.designation).filter(Boolean)).size;

  const designationCounts = useMemo(() => {
    const counts = {};
    staff.forEach(s => {
      const d = s.designation || 'Other';
      counts[d] = (counts[d] || 0) + 1;
    });
    return counts;
  }, [staff]);

  const visibleDesignations = useMemo(() => {
    return [...new Set(staff.map(s => s.designation).filter(Boolean))].sort();
  }, [staff]);

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div>
      {/* ─── Header ─── */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">
          <i className="fas fa-users-cog text-purple-500 mr-2"></i>
          Staff Management
        </h2>
        <div className="flex gap-2">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 text-sm"
          >
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {canEdit && (
            <button
              onClick={() => { resetForm(); setModalOpen(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
            >
              <i className="fas fa-plus mr-1"></i> Add Staff
            </button>
          )}
        </div>
      </div>

      {/* ─── Stats Row ─── */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white p-4 rounded shadow border-l-4 border-purple-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-users text-purple-500"></i> Total Staff
          </p>
          <p className="text-2xl font-bold text-purple-700">{filtered.length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-money-bill-wave text-green-500"></i> Monthly Salary
          </p>
          <p className="text-2xl font-bold text-green-700">
            {CURRENCY}{totalMonthlySalary.toLocaleString()}
          </p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-indigo-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-tags text-indigo-500"></i> Designations
          </p>
          <p className="text-2xl font-bold text-indigo-700">{uniqueDesignations}</p>
        </div>
      </div>

      {/* ─── Campus Filter Cards ─── */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setFilterCampus('all')}
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            filterCampus === 'all'
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-building text-gray-700"></i> All Campuses
          </p>
          <p className="text-2xl font-bold text-gray-800">{staff.length}</p>
        </button>

        {campusCounts.map(c => {
          const isActive = filterCampus === String(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFilterCampus(prev => prev === String(c.id) ? 'all' : String(c.id))}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? 'border-purple-500 ring-2 ring-offset-1 ring-gray-300 bg-purple-50'
                  : 'border-purple-200 hover:border-purple-500 hover:shadow-md'
              }`}
            >
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className="fas fa-school text-purple-600"></i> {c.name}
              </p>
              <p className="text-2xl font-bold text-purple-700">{c.count}</p>
            </button>
          );
        })}
      </div>

      {/* ─── Filter bar ─── */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select
          className="p-2 border rounded text-sm"
          value={filterCampus}
          onChange={e => setFilterCampus(e.target.value)}
        >
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select
          className="p-2 border rounded text-sm"
          value={filterDesignation}
          onChange={e => setFilterDesignation(e.target.value)}
        >
          <option value="all">All Designations</option>
          {visibleDesignations.map(d => (
            <option key={d} value={d}>{d} ({designationCounts[d] || 0})</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search by name, phone, CNIC, designation..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <span className="ml-auto text-sm text-gray-500">
          Showing {filtered.length} of {staff.length}
        </span>
      </div>

      {/* ─── Table ─── */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-users-cog text-4xl mb-3"></i>
            <p>No staff found.</p>
            {canEdit && (
              <p className="text-sm mt-2">Click "Add Staff" to create the first one.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Designation', 'Phone', 'CNIC', 'Salary', 'Campus', 'Join Date', 'Status', 'Actions'].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const dColor = DESIGNATION_COLORS[s.designation] || 'bg-gray-100 text-gray-700';
                  return (
                    <tr key={s.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 font-medium">
                        {s.first_name} {s.last_name}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${dColor}`}>
                          {s.designation}
                        </span>
                      </td>
                      <td className="p-3">{s.phone || '—'}</td>
                      <td className="p-3 font-mono text-xs">{s.cnic || '—'}</td>
                      <td className="p-3 font-semibold text-green-700">
                        {CURRENCY}{(s.salary || 0).toLocaleString()}
                      </td>
                      <td className="p-3 text-xs text-gray-600">
                        {getCampusName(s.campus_id)}
                      </td>
                      <td className="p-3 text-xs text-gray-600">
                        {s.join_date || '—'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {canEdit && (
                            <>
                              <button
                                onClick={() => openEdit(s)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                title="Edit"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(s)}
                                className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                                title="Deactivate"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Add / Edit Modal ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={editing ? `Edit Staff — ${editing.first_name} ${editing.last_name}` : 'Add Staff'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
        size="lg"
      >
        <div className="space-y-3">
          {error && (
            <div className="bg-red-50 text-red-600 p-2 rounded text-sm border border-red-200">
              <i className="fas fa-exclamation-circle mr-1"></i> {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">First Name *</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="First Name"
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Last Name *</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="Last Name"
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Designation *</label>
            <select
              className="w-full p-2 border rounded"
              value={form.designation}
              onChange={e => setForm({ ...form, designation: e.target.value })}
            >
              {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Phone</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="+92-300-1234567"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">CNIC</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="42101-1234567-8"
                value={form.cnic}
                onChange={e => setForm({ ...form, cnic: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Address</label>
            <input
              className="w-full p-2 border rounded"
              placeholder="Address"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Join Date</label>
              <input
                type="date"
                className="w-full p-2 border rounded"
                value={form.join_date}
                onChange={e => setForm({ ...form, join_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Monthly Salary ({CURRENCY.trim()})</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                placeholder="0"
                value={form.salary}
                onChange={e => setForm({ ...form, salary: e.target.value })}
              />
            </div>
          </div>

          {/* Campus — auto-assigned for principal/accountant, shown only if admin/superintendent */}
          {!['principal', 'accountant'].includes(role) && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Campus</label>
              <select
                className="w-full p-2 border rounded"
                value={form.campus_id}
                onChange={e => setForm({ ...form, campus_id: e.target.value })}
              >
                <option value="">— Select campus —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {['principal', 'accountant'].includes(role) && (
            <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
              <i className="fas fa-info-circle mr-1"></i>
              Staff will be assigned to your campus automatically.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}